import { supabase } from '@/service/supabaseClient';
import type { AgricobotAsset } from '@/types/AgricobotAsset';
import type { InboundRecord } from '@/types/GenericAsset';
import {
  AgricobotCompanionAsset,
  type AgricobotCompanionHeartbeatData,
  type AgricobotCompanionTelemetryData,
  getAllAgricobotCompanionMessageTypes,
} from '../assets/AgricobotCompanion';
import { AgricobotCompanionMessageTypes } from '../assets/AgricobotCompanion';
import type { AgricobotCompanionHeartbeatDataArray } from '../types/CompanionHeartbeatArray';
import type { AgricobotCompanionTelemetryDataArray } from '../types/CompanionTelemetryArray';

/**
 * Provides services for managing and retrieving Agricobot companion device data.
 * Encapsulates logic for fetching from Supabase and processing array-based records
 */
export class AgricobotCompanionServices {
  /**
   * Retrieves a single AgricobotCompanionAsset from **Database** by its identifiers.
   * This method fetches companion registration details and its latest message types.
   * from the database, then constructs a companion asset instance.
   * @param {string} node_id - The node ID of the master asset.
   * @param {string} asset_id - The primary asset ID of the master asset.
   * @param {string} device_id - The device ID of the master asset.
   * @param {string} companion_id - The specific ID of the companion to retrieve.
   *
   * @returns {Promise<AgricobotCompanionAsset | null>} A promise that resolves to an `AgricobotCompanionAsset` instance, or `null` if not found or an error occurs.
   */
  public async getCompanion(
    asset: AgricobotAsset,
    companion_id: string,
  ): Promise<AgricobotCompanionAsset | null> {
    const device_id = asset.device_id;
    const node_id = asset.node_id;
    const asset_id = asset.asset_id;

    // Get from COMPANION_REGISTRY
    // First, verify the companion exists in the central registry.
    const { data: companionRegistry, error: companionRegistryError } = await supabase
      .from('companion_registry')
      .select('*')
      .eq('device_id', device_id)
      .eq('companion_id', companion_id);
    if (companionRegistryError) {
      console.error('Error fetching companion data', companionRegistryError);
      return null;
    }

    if (companionRegistry.length === 0) return null;

    // Get from INBOUND
    // If registered, fetch the last 12 relevant inbound messages for this device.
    // This includes both heartbeat and telemetry data that contain the specified companion.
    const { data: inboundRecords, error: inboundRecordsError } = await supabase
      .from('inbound')
      .select('*')
      .eq('node_id', node_id)
      .eq('asset_id', asset_id)
      .eq('device_id', device_id)
      .in('message_type', getAllAgricobotCompanionMessageTypes())
      .contains('payload', { companion_ids: [companion_id] })
      // .in('payload:jsonb->companion_ids', [companion_id])
      .order('received_at', { ascending: false })
      .limit(12);

    if (inboundRecordsError) {
      console.error('Error fetching inbound records', companionRegistryError);
      return null;
    }

    if (inboundRecords.length === 0) {
      console.error('Error: inbound length === 0');
      return null;
    }

    const distinctTypeSingleInboundRecords: InboundRecord[] = [];
    for (const messageType of getAllAgricobotCompanionMessageTypes()) {
      const inboundRecordFiltered = this.findRecordByTypeAndCompanionId(
        inboundRecords as InboundRecord[],
        messageType,
        companion_id,
      );
      if (inboundRecordFiltered && inboundRecordFiltered.message_type === messageType) {
        const singleInboundRecord = this.inboundRecordArrayToSingle(
          companion_id,
          inboundRecordFiltered as InboundRecord,
          inboundRecordFiltered as InboundRecord,
        );
        if (singleInboundRecord) {
          distinctTypeSingleInboundRecords.push(singleInboundRecord);
        }
      }
    }

    // Expect a record for each message type.
    if (distinctTypeSingleInboundRecords.length === 0) return null;

    // If all necessary data is present, create a new companion asset instance.
    const companionAsset = new AgricobotCompanionAsset(distinctTypeSingleInboundRecords, asset);

    return companionAsset;
  }

  /**
   * Retrieves all AgricobotCompanionAssets for a given device from the database.
   * This method fetches all registered companions for the device, then retrieves
   * recent inbound records and processes them to create companion assets.
   * @param {string} node_id - The node ID of the master asset.
   * @param {string} asset_id - The primary asset ID of the master asset.
   * @param {string} device_id - The device ID of the master asset.
   * @returns {Promise<AgricobotCompanionAsset[]>} A promise that resolves to an array of `AgricobotCompanionAsset` instances.
   */
  public async getAllCompanions(asset: AgricobotAsset): Promise<AgricobotCompanionAsset[]> {
    const node_id = asset.node_id;
    const asset_id = asset.asset_id;
    const device_id = asset.device_id;

    // Fetch all registered companions for this device
    const { data: companionsRegistry, error: registryError } = await supabase
      .from('companion_registry')
      .select('*')
      .eq('device_id', device_id);

    if (registryError || !companionsRegistry?.length) {
      console.error('Error fetching companion registry', registryError);
      return [];
    }

    // Extract the list of companion_ids
    const companionIds = companionsRegistry.map((c) => c.companion_id);

    // Fetch latest 100 inbound records for companion message types
    const { data: inboundRecords, error: inboundError } = await supabase
      .from('inbound')
      .select('*')
      .eq('node_id', node_id)
      .eq('asset_id', asset_id)
      .eq('device_id', device_id)
      .in('message_type', getAllAgricobotCompanionMessageTypes())
      .contains('payload', { companion_ids: companionIds }) // Filter by companion_ids retrieved
      .order('received_at', { ascending: false })
      .limit(100);

    if (inboundError || !inboundRecords?.length) {
      console.error('Error fetching inbound records', inboundError);
      return [];
    }

    const inboundRecordsTyped = inboundRecords as InboundRecord[];

    const companions: AgricobotCompanionAsset[] = [];

    for (const companion_id of companionIds) {
      // Filter records that contain this companion_id
      const filteredRecords = inboundRecordsTyped.filter((record) => {
        const payload = record.payload as { companion_ids?: string[] };
        return payload.companion_ids?.includes(companion_id);
      });

      // For each message type, find the latest record that matches the type
      const distinctTypeSingleInboundRecords: InboundRecord[] = [];

      for (const messageType of getAllAgricobotCompanionMessageTypes()) {
        // Find the first (latest) record with this type
        const inboundRecordFiltered = filteredRecords.find(
          (record) => record.message_type === messageType,
        );

        if (inboundRecordFiltered) {
          const singleInboundRecord = this.inboundRecordArrayToSingle(
            companion_id,
            inboundRecordFiltered,
            inboundRecordFiltered,
          );

          if (singleInboundRecord) {
            distinctTypeSingleInboundRecords.push(singleInboundRecord);
          }
        }
      }

      // If there are records, create the companion asset
      if (distinctTypeSingleInboundRecords.length > 0) {
        const companionAsset = new AgricobotCompanionAsset(distinctTypeSingleInboundRecords, asset);
        companions.push(companionAsset);
      }
    }

    return companions;
  }

  /** ===============================================
   * Utility functions
   * =============================================== */

  /**
   * This is a utility function.
   *
   * Finds the first inbound record from a list that matches the passed **message type** and **contains a given companion ID** in its payload (companion_ids).
   * This is used to locate the correct array-based record before deconstructing its payload.
   * @param {InboundRecord[]} inbound - An array of inbound records to search through.
   * @param {AgricobotCompanionMessageTypes} message_type - The message type to filter by.
   * @param {string} companion_id - The companion ID to find within the record's payload.
   * @returns {InboundRecord | null} The found inbound record, or `null` if no match is found.
   */
  findRecordByTypeAndCompanionId(
    inbound: InboundRecord[],
    message_type: AgricobotCompanionMessageTypes,
    companion_id: string,
  ): InboundRecord | null {
    interface InboundRecordWithCompanionIds {
      payload: {
        companion_ids: string[];
      };
    }

    const inboundHeartbeatRecord = inbound.find((item) => {
      const payload = item.payload as InboundRecordWithCompanionIds['payload'];
      // A record matches if it has the correct message type and its payload's
      // companion_ids array includes the target companion_id.
      if (item.message_type !== message_type) return false;
      // Check if payload contains companion_id
      return payload.companion_ids.includes(companion_id);
    });

    if (!inboundHeartbeatRecord) return null;

    return inboundHeartbeatRecord;
  }

  isInboundRecordAnArray(inbound: InboundRecord): boolean {
    // Message type doesn't match any of the supported ones
    if (
      !Object.values(AgricobotCompanionMessageTypes).includes(
        inbound.message_type as AgricobotCompanionMessageTypes,
      )
    ) {
      console.error(
        'isInboundRecordAnArray: Inbound record is not a companion message type',
        inbound.message_type,
      );
      return false;
    }
    switch (inbound.message_type) {
      case AgricobotCompanionMessageTypes.HEARTBEAT:
        return inbound.payload.companion_ids !== undefined;
      case AgricobotCompanionMessageTypes.TELEMETRY_DATA:
        return inbound.payload.companion_ids !== undefined;
      default:
        throw new Error(
          `Unimplemented message type in isInboundRecordAnArray: ${inbound.message_type}`,
        );
    }
  }

  /**
   * Dispatches the deconstruction of an array-based inbound record to the correct handler based on message type.
   * @param {string} companion_id - The ID of the companion to extract data for.
   * @param {InboundRecord} baseRecord - The base record to use for shared properties.
   * @param {InboundRecord} inbound - The inbound record containing the array-based payload.
   * @returns {InboundRecord | null} A new inbound record with a single-companion payload, or `null`.
   */
  inboundRecordArrayToSingle(
    companion_id: string,
    baseRecord: InboundRecord,
    inbound: InboundRecord,
  ): InboundRecord | null {
    // Selects the appropriate deconstruction method based on the message type.
    switch (inbound.message_type) {
      case AgricobotCompanionMessageTypes.HEARTBEAT:
        return this.inboundArrayHeartbeatToSingle(companion_id, baseRecord, inbound);
      case AgricobotCompanionMessageTypes.TELEMETRY_DATA:
        return this.inboundArrayTelemetryToSingle(companion_id, baseRecord, inbound);
      default:
        throw new Error(`Unimplemented message type: ${inbound.message_type}`);
    }
  }

  /**
   * Deconstructs an array-based heartbeat record into a single-companion heartbeat record.
   * It finds the specific companion's data within the payload arrays and creates a new record.
   * @param {string} companion_id - The ID of the companion to extract data for.
   * @param {InboundRecord} baseRecord - The base record providing common properties.
   * @param {InboundRecord} inbound - The inbound record with the array-based heartbeat payload.
   * @returns {InboundRecord | null} A new inbound record with a single-companion heartbeat payload, or `null` if the companion ID is not found.
   */
  inboundArrayHeartbeatToSingle(
    companion_id: string,
    baseRecord: InboundRecord,
    inbound: InboundRecord,
  ): InboundRecord | null {
    const payload = inbound.payload as AgricobotCompanionHeartbeatDataArray;

    // Find the index of the target companion within the payload's ID array.
    const index = payload.companion_ids?.indexOf(companion_id) ?? -1;

    if (index === -1) return null;

    // Create a new record with a payload containing only the data for the specified companion.
    return {
      ...baseRecord,
      payload: {
        frame_id: baseRecord.payload.frame_id,
        robot_id: baseRecord.payload.robot_id,
        companion_id: payload.companion_ids?.[index] ?? null,
        companion_model: payload.companion_models?.[index] ?? null,
        companion_status: payload.companion_statuses?.[index] ?? null,
        companion_sftw_version: payload.companion_sftw_versions?.[index] ?? null,
      },
    };
  }

  /**
   * Deconstructs an array-based telemetry record into a single-companion telemetry record.
   * It extracts the data for a specific companion from the various telemetry arrays in the payload.
   * @param {string} companion_id - The ID of the companion for which to extract data.
   * @param {InboundRecord} baseRecord - The base record providing common properties like frame_id and robot_id.
   * @param {InboundRecord} inbound - The inbound record containing the array-based telemetry payload.
   * @returns {InboundRecord | null} A new inbound record with a single-companion telemetry payload, or `null` if the companion ID is not found.
   */
  inboundArrayTelemetryToSingle(
    companion_id: string,
    baseRecord: InboundRecord,
    inbound: InboundRecord,
  ): InboundRecord | null {
    const payload = inbound.payload as AgricobotCompanionTelemetryDataArray;

    // Find the index of the target companion to access its data in the parallel arrays.
    const index = payload.companion_ids?.indexOf(companion_id) ?? -1;

    if (index === -1) return null;

    // Construct a new record with a single-companion telemetry payload.
    // Each property is extracted from its respective array using the found index.
    return {
      ...baseRecord,
      payload: {
        frame_id: baseRecord.payload.frame_id,
        robot_id: baseRecord.payload.robot_id,
        timestamp: baseRecord.payload.timestamp,
        pos_x_ecef: payload.pos_x_ecef_array?.[index] ?? null,
        pos_y_ecef: payload.pos_y_ecef_array?.[index] ?? null,
        pos_z_ecef: payload.pos_z_ecef_array?.[index] ?? null,
        vel_x_ms: payload.vel_x_ms_array?.[index] ?? null,
        rot_x: payload.rot_x_array?.[index] ?? null,
        rot_y: payload.rot_y_array?.[index] ?? null,
        rot_z: payload.rot_z_array?.[index] ?? null,
        rot_w: payload.rot_w_array?.[index] ?? null,
        solution_type: payload.solution_type_array?.[index] ?? null,
        vision_status:
          payload.vision_status_array?.[index] !== undefined
            ? Boolean(payload.vision_status_array[index])
            : null,
        anti_collision_status: payload.anti_collision_status_array?.[index] ?? null,
        identified_obstacle_type: payload.identified_obstacle_type_array?.[index] ?? null,
        engine_status: payload.engine_status_array?.[index] ?? null,
        engine_rpm: payload.engine_rpm_array?.[index] ?? null,
        fuel_level_percent: payload.fuel_level_percent_array?.[index] ?? null,
        ecu_voltage_v: payload.ecu_voltage_v_array?.[index] ?? null,
        water_tank_level_percent: payload.water_tank_level_percent_array?.[index] ?? null,
        attachment_type: payload.attachment_type_array?.[index] ?? null,
        attachment_status: payload.attachment_status_array?.[index] ?? null,
        sprayer_left_status: payload.sprayer_left_status_array?.[index] ?? null,
        sprayer_right_status: payload.sprayer_right_status_array?.[index] ?? null,
        pump_pressure_bar: payload.pump_pressure_bar_array?.[index] ?? null,
        flow_rate_l_min: payload.flow_rate_l_min_array?.[index] ?? null,
        plan_name: payload.plan_name_array?.[index] ?? null,
        plan_status: payload.plan_status_array?.[index] ?? null,
        plan_completion_percent: payload.plan_completion_percent_array?.[index] ?? null,
        anomaly_id: payload.anomaly_id_array?.[index] ?? null,
        anomaly_type: payload.anomaly_type_array?.[index] ?? null,
        data_quality: null, // This is not present in the array-based telemetry
      },
    };
  }

  /**
   * Creates an array of AgricobotCompanionAsset instances from aggregated heartbeat and telemetry data.
   * This method deconstructs the array-based data into individual records for each companion,
   * then uses them to instantiate AgricobotCompanionAsset objects.
   *
   * @param {AgricobotCompanionHeartbeatDataArray} heartbeatData - The aggregated heartbeat data for all companions.
   * @param {AgricobotCompanionTelemetryDataArray} telemetryData - The aggregated telemetry data for all companions.
   * @param {InboundRecord} baseRecord - A base InboundRecord to source common data like node_id, device_id, etc.
   * @returns {AgricobotCompanionAsset[]} An array of instantiated AgricobotCompanionAsset.
   */
  public createCompanionsFromArray(
    heartbeatData: AgricobotCompanionHeartbeatDataArray,
    telemetryData: AgricobotCompanionTelemetryDataArray,
    baseRecord: InboundRecord,
    asset: AgricobotAsset,
  ): AgricobotCompanionAsset[] {
    const companions: AgricobotCompanionAsset[] = [];
    const companionCount = heartbeatData.companion_count ?? telemetryData.companion_count ?? 0;

    // Validate that the essential data arrays are present before proceeding.
    if (companionCount === 0 && !heartbeatData.companion_ids && !telemetryData.companion_ids) {
      console.error('Invalid companion data', {
        companionCount,
        heartbeatData,
      });
      return [];
    }

    // Create companion assets
    // Iterate through each companion reported in the count.
    for (let i = 0; i < companionCount; i++) {
      // Create a payload for a single companion's heartbeat by extracting data from the arrays at the current index.
      const singleHeartbeatPayload: AgricobotCompanionHeartbeatData = {
        frame_id: heartbeatData.frame_id,
        robot_id: heartbeatData.robot_id,
        companion_id: heartbeatData.companion_ids?.[i] ?? null,
        companion_model: heartbeatData.companion_models?.[i] ?? null,
        companion_status: heartbeatData.companion_statuses?.[i] ?? null,
        companion_sftw_version: heartbeatData.companion_sftw_versions?.[i] ?? null,
      };

      // Create a payload for a single companion's telemetry data.
      const singleTelemetryPayload: AgricobotCompanionTelemetryData = {
        frame_id: telemetryData.frame_id,
        robot_id: telemetryData.robot_id,
        timestamp: telemetryData.timestamp ? new Date(telemetryData.timestamp / 1_000_000) : null,
        pos_x_ecef: telemetryData.pos_x_ecef_array?.[i] ?? null,
        pos_y_ecef: telemetryData.pos_y_ecef_array?.[i] ?? null,
        pos_z_ecef: telemetryData.pos_z_ecef_array?.[i] ?? null,
        vel_x_ms: telemetryData.vel_x_ms_array?.[i] ?? null,
        rot_x: telemetryData.rot_x_array?.[i] ?? null,
        rot_y: telemetryData.rot_y_array?.[i] ?? null,
        rot_z: telemetryData.rot_z_array?.[i] ?? null,
        rot_w: telemetryData.rot_w_array?.[i] ?? null,
        solution_type: telemetryData.solution_type_array?.[i] ?? null,
        vision_status:
          telemetryData.vision_status_array?.[i] !== undefined
            ? Boolean(telemetryData.vision_status_array[i])
            : null,
        anti_collision_status: telemetryData.anti_collision_status_array?.[i] ?? null,
        identified_obstacle_type: telemetryData.identified_obstacle_type_array?.[i] ?? null,
        engine_status: telemetryData.engine_status_array?.[i] ?? null,
        engine_rpm: telemetryData.engine_rpm_array?.[i] ?? null,
        fuel_level_percent: telemetryData.fuel_level_percent_array?.[i] ?? null,
        ecu_voltage_v: telemetryData.ecu_voltage_v_array?.[i] ?? null,
        water_tank_level_percent: telemetryData.water_tank_level_percent_array?.[i] ?? null,
        attachment_type: telemetryData.attachment_type_array?.[i] ?? null,
        attachment_status: telemetryData.attachment_status_array?.[i] ?? null,
        sprayer_left_status: telemetryData.sprayer_left_status_array?.[i] ?? null,
        sprayer_right_status: telemetryData.sprayer_right_status_array?.[i] ?? null,
        pump_pressure_bar: telemetryData.pump_pressure_bar_array?.[i] ?? null,
        flow_rate_l_min: telemetryData.flow_rate_l_min_array?.[i] ?? null,
        plan_name: telemetryData.plan_name_array?.[i] ?? null,
        plan_status: telemetryData.plan_status_array?.[i] ?? null,
        plan_completion_percent: telemetryData.plan_completion_percent_array?.[i] ?? null,
        anomaly_id: telemetryData.anomaly_id_array?.[i] ?? null,
        anomaly_type: telemetryData.anomaly_type_array?.[i] ?? null,
        data_quality: null, // This is not present in the array-based telemetry
      };

      // Create a synthetic inbound record for the heartbeat data.
      const heartbeatInboundRecord: InboundRecord = {
        ...baseRecord,
        message_type: AgricobotCompanionMessageTypes.HEARTBEAT,
        payload: singleHeartbeatPayload,
      };

      // Create a synthetic inbound record for the telemetry data.
      const telemetryInboundRecord: InboundRecord = {
        ...baseRecord,
        message_type: AgricobotCompanionMessageTypes.TELEMETRY_DATA,
        payload: singleTelemetryPayload,
        received_at: telemetryData.timestamp
          ? new Date(telemetryData.timestamp / 1_000_000).toISOString()
          : baseRecord.received_at,
      };

      // Instantiate a new companion asset using the individual heartbeat and telemetry records.
      const companionAsset = new AgricobotCompanionAsset(
        [heartbeatInboundRecord, telemetryInboundRecord],
        asset,
      );
      companions.push(companionAsset);
    }

    return companions;
  }

  public updateCompanions(
    companions: AgricobotCompanionAsset[],
    inboundRecords: InboundRecord[],
  ): void {
    // Quick exits
    if (!companions?.length || !inboundRecords?.length) {
      return;
    }

    // Work on a time-desc sorted copy so "first match" == latest
    const sortedInbound = [...inboundRecords].sort(
      (a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime(),
    );

    for (const companion of companions) {
      const companionId = companion.companionId;
      if (!companionId) {
        continue;
      }

      const recordsToApply: InboundRecord[] = [];

      for (const messageType of getAllAgricobotCompanionMessageTypes()) {
        const found = this.findRecordByTypeAndCompanionId(
          sortedInbound as InboundRecord[],
          messageType,
          companionId,
        );

        if (!found) continue;

        // If the record is already single-companion (has companion_id), use it directly
        const payloadObj = (found.payload ?? {}) as Record<string, unknown>;
        const hasSingleCompanion = Object.prototype.hasOwnProperty.call(payloadObj, 'companion_id');

        if (hasSingleCompanion) {
          recordsToApply.push(found);
          continue;
        }

        // Convert array-based payload to a single-companion record
        const single = this.inboundRecordArrayToSingle(companionId, found, found);
        if (single) {
          recordsToApply.push(single);
        }
      }

      if (recordsToApply.length > 0) {
        companion.updateInboundRecord(recordsToApply);
      }
    }
  }
}

/**
 * Extension methods for exporting companion telemetry over time ranges.
 */
export interface DateRange {
  start?: Date;
  end?: Date;
}

export type MessageType = string;

export class AgricobotCompanionExportQueryService extends AgricobotCompanionServices {
  /**
   * Fetch inbound records for a specific companion within a date range and convert
   * array-based payloads to single-companion payloads when necessary.
   */
  public async getInboundRecordsForCompanion(
    asset: AgricobotAsset,
    companion_id: string,
    messageTypes: MessageType[],
    dateRange?: DateRange,
  ): Promise<InboundRecord[]> {
    const node_id = asset.node_id;
    const asset_id = asset.asset_id;
    const device_id = asset.device_id;

    let query = supabase
      .from('inbound')
      .select('*')
      .eq('node_id', node_id)
      .eq('asset_id', asset_id)
      .eq('device_id', device_id)
      .in('message_type', messageTypes)
      .contains('payload', { companion_ids: [companion_id] })
      .order('received_at', { ascending: true })
      .limit(10000);

    if (dateRange?.start) {
      query = query.gte('received_at', dateRange.start.toISOString());
    }

    if (dateRange?.end) {
      query = query.lte('received_at', dateRange.end.toISOString());
    }

    const { data, error } = await query;

    if (error || !data?.length) {
      return [];
    }

    const inbound = data as InboundRecord[];

    const singleCompanionRecords: InboundRecord[] = [];
    for (const record of inbound) {
      const payloadObj = (record.payload ?? {}) as Record<string, unknown>;
      const hasSingleCompanion = Object.prototype.hasOwnProperty.call(payloadObj, 'companion_id');
      if (hasSingleCompanion) {
        singleCompanionRecords.push(record);
        continue;
      }

      const single = this.inboundRecordArrayToSingle(companion_id, record, record);
      if (single) {
        singleCompanionRecords.push(single);
      }
    }

    return singleCompanionRecords;
  }
}
